import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { iniciales } from '../lib/formato.js';
import { txt, TXT_PERFIL } from '../lib/enums.js';
import { aplicarTema, temaGuardado, type Tema } from '../lib/tema.js';
import { VISTAS, vistaDe } from '../vistas.js';
import { useFiltros } from '../lib/filtros.jsx';
import { useSesion } from './Sesion.jsx';
import { api } from '../lib/api.js';

/**
 * La campana del encabezado.
 *
 * Antes mostraba un `3` escrito a mano. Eso es peor que no tener campana:
 * parece que algo vigila y no vigila nada, asi que quien la mira aprende a
 * no mirarla justo el dia que de verdad hay tres.
 *
 * Ahora cuenta las desviaciones SIN ATENDER que esta persona alcanza —la
 * vista respeta RLS, asi que un cliente cuenta las suyas— y respeta los
 * filtros globales, como el resto de la plataforma.
 */
function Campana(): JSX.Element | null {
  const { puede } = useSesion();
  const { consulta } = useFiltros();
  const [pendientes, setPendientes] = useState<number | null>(null);
  const alcanza = puede('MOD_TORRE');

  useEffect(() => {
    if (!alcanza) return undefined;
    let vivo = true;
    const pedir = (): void => {
      api
        .obtener<{ pendientes: number }>(`/v1/alertas/resumen${consulta}`)
        /* Si falla, la campana se calla. Un numero viejo seria mentir con
           mas trabajo que no decir nada. */
        .then(({ datos }) => vivo && setPendientes(datos.pendientes))
        .catch(() => vivo && setPendientes(null));
    };
    pedir();
    /* Cada dos minutos. No es un tablero en vivo: la torre lo es, y esta
       campana solo dice si vale la pena abrirla. Preguntar cada pocos
       segundos desde cada pantalla seria cobrarle a la base por una cifra
       que cambia despacio. */
    const t = setInterval(pedir, 120_000);
    return () => {
      vivo = false;
      clearInterval(t);
    };
  }, [alcanza, consulta]);

  if (!alcanza) return null;

  return (
    <Link
      to="/torre"
      className="icobtn"
      aria-label={
        pendientes === null
          ? 'Torre de control'
          : `Torre de control, ${pendientes} desviacion(es) sin atender`
      }
      data-prueba="campana"
    >
      🔔
      {pendientes !== null && pendientes > 0 && (
        <span className="dot" data-prueba="campana-cuenta">
          {pendientes > 99 ? '99+' : pendientes}
        </span>
      )}
    </Link>
  );
}

/**
 * Las tres piezas canonicas del estandar: topbar, sidebar y pagehead.
 * Siempre iguales, en ese orden, en todas las pantallas.
 */

/**
 * Los tres filtros globales, en el encabezado.
 *
 * Van aqui y no dentro de cada pantalla porque mandan sobre todas: se eligen
 * una vez y siguen puestos al navegar.
 */
function FiltrosGlobales(): JSX.Element | null {
  const { usuario } = useSesion();
  const { filtros, poner, limpiar, hayAlguno, clientes, ciudades, proyectos, clienteFijo } =
    useFiltros();

  if (!usuario) return null;

  return (
    <div className="tb-filtros" role="group" aria-label="Filtros globales">
      {/* Al perfil cliente NO se le dibuja el selector.
      
          La primera version lo mostraba deshabilitado, y eso resulto peor que
          no mostrarlo: ese perfil no alcanza MOD_CLIENTES, asi que la lista
          llega vacia y el control se queda diciendo "Todos los clientes"
          cuando lo que pasa es exactamente lo contrario —solo ve el suyo—.
          Un control deshabilitado que afirma lo contrario de la verdad
          desinforma mas que su ausencia. Su alcance lo fija la RLS en la
          base; aqui no hay nada que elegir. */}
      {!clienteFijo && (
        <>
          <label className="visualmente-oculto" htmlFor="f-cliente">
            Filtrar por cliente
          </label>
          <select
            id="f-cliente"
            value={filtros.cliente}
            onChange={(e) => poner('cliente', e.target.value)}
            data-prueba="filtro-cliente"
          >
            <option value="">Todos los clientes</option>
            {clientes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nombre}
              </option>
            ))}
          </select>
        </>
      )}

      <label className="visualmente-oculto" htmlFor="f-ciudad">
        Filtrar por ciudad
      </label>
      <select
        id="f-ciudad"
        value={filtros.ciudad}
        onChange={(e) => poner('ciudad', e.target.value)}
        data-prueba="filtro-ciudad"
      >
        <option value="">Todas las ciudades</option>
        {ciudades.map((c) => (
          <option key={c.id} value={c.id}>
            {c.nombre}
          </option>
        ))}
      </select>

      <label className="visualmente-oculto" htmlFor="f-proyecto">
        Filtrar por proyecto
      </label>
      <select
        id="f-proyecto"
        value={filtros.proyecto}
        onChange={(e) => poner('proyecto', e.target.value)}
        data-prueba="filtro-proyecto"
      >
        <option value="">Todos los proyectos</option>
        {proyectos.map((p) => (
          <option key={p.id} value={p.id}>
            {p.nombre}
          </option>
        ))}
      </select>

      {hayAlguno && (
        <button
          type="button"
          className="btn ghost sm"
          onClick={limpiar}
          data-prueba="limpiar-filtros"
        >
          Limpiar
        </button>
      )}
    </div>
  );
}

function Topbar({ tema, cambiarTema }: { tema: Tema; cambiarTema: () => void }): JSX.Element {
  const { usuario, salir } = useSesion();

  return (
    <header className="tb">
      <div className="tb-brand">
        <span className="mark" aria-hidden="true">
          E
        </span>
        <span>SCM IDC</span>
      </div>

      <div className="tb-search">
        <span className="lupa" aria-hidden="true">
          🔍
        </span>
        <label className="visualmente-oculto" htmlFor="buscador">
          Buscar en la plataforma
        </label>
        <input
          id="buscador"
          type="search"
          placeholder="Buscar folio, serie, guia o sitio…"
          data-prueba="buscador"
        />
      </div>

      <FiltrosGlobales />

      <div className="tb-actions">
        <button
          type="button"
          className="icobtn"
          onClick={cambiarTema}
          /* El control dice a que tema lleva, no en cual esta: es lo que la
             persona quiere saber antes de presionarlo. */
          aria-label={tema === 'dark' ? 'Cambiar a tema claro' : 'Cambiar a tema oscuro'}
          data-prueba="tema"
        >
          {tema === 'dark' ? '☀' : '🌙'}
        </button>
        <Campana />
      </div>

      {usuario && (
        <div className="tb-user">
          <span className="avatar sm" aria-hidden="true">
            {iniciales(usuario.nombre)}
          </span>
          <span className="quien">
            <b data-prueba="usuario">{usuario.nombre}</b>
            <span className="muted" style={{ fontSize: 11 }} data-prueba="perfil">
              {txt(TXT_PERFIL, usuario.perfil)}
            </span>
          </span>
          <button type="button" className="btn ghost sm" onClick={() => void salir()}>
            Salir
          </button>
        </div>
      )}
    </header>
  );
}

function Sidebar({ tema, cambiarTema }: { tema: Tema; cambiarTema: () => void }): JSX.Element {
  const { puede } = useSesion();
  const { pathname } = useLocation();

  /* La nav se filtra por permisos y un grupo que se queda sin items NO se
     dibuja: un encabezado huerfano hace pensar que algo falta por cargar. */
  const grupos = VISTAS.map((g) => ({
    ...g,
    items: g.items.filter((v) => puede(v.modulo)),
  })).filter((g) => g.items.length > 0);

  return (
    <nav className="sb" aria-label="Navegacion principal">
      <div className="nav">
        {grupos.map((g) => (
          <div key={g.g}>
            <div className="lbl">{g.g}</div>
            {g.items.map((v) => {
              const activa = v.ruta === '/' ? pathname === '/' : pathname.startsWith(v.ruta);
              return (
                <Link
                  key={v.ruta}
                  to={v.ruta}
                  className={activa ? 'on' : undefined}
                  aria-current={activa ? 'page' : undefined}
                  data-prueba={`nav-${v.ruta}`}
                >
                  <span className="ic" aria-hidden="true">
                    {v.ic}
                  </span>
                  <span>{v.n}</span>
                </Link>
              );
            })}
          </div>
        ))}
      </div>

      {/* El segundo control de tema que pide el estandar. */}
      <div className="sb-foot">
        <div className="switch" role="group" aria-label="Tema de la interfaz">
          <button
            type="button"
            className={tema === 'light' ? 'on' : undefined}
            onClick={() => tema !== 'light' && cambiarTema()}
          >
            Dia
          </button>
          <button
            type="button"
            className={tema === 'dark' ? 'on' : undefined}
            onClick={() => tema !== 'dark' && cambiarTema()}
          >
            Noche
          </button>
        </div>
      </div>
    </nav>
  );
}

export function Pagehead({ acciones }: { acciones?: ReactNode }): JSX.Element {
  const { pathname } = useLocation();
  const v = vistaDe(pathname);

  return (
    <div className="pagehead">
      <div>
        <h1>{v?.t ?? 'SCM IDC'}</h1>
        <div className="crumb">
          Inicio {v && v.ruta !== '/' ? `› ${v.t}` : ''} {v ? `· ${v.s}` : ''}
        </div>
      </div>
      {acciones && <div className="acciones">{acciones}</div>}
    </div>
  );
}

export function Cascaron({ children }: { children: ReactNode }): JSX.Element {
  const [tema, setTema] = useState<Tema>(() => temaGuardado());

  useEffect(() => {
    aplicarTema(tema);
  }, [tema]);

  const cambiarTema = (): void => setTema((t) => (t === 'dark' ? 'light' : 'dark'));

  return (
    <div className="app">
      <Topbar tema={tema} cambiarTema={cambiarTema} />
      <Sidebar tema={tema} cambiarTema={cambiarTema} />
      <main>{children}</main>
    </div>
  );
}
